import { valueToDatabase } from '@databases/collab/convert/to-database';
import { type YDatabase } from '@databases/collab/y-database';
import { type ValueSdkContract } from '@databases/sdk-types';
import { DEFAULT_ENTRY_ID } from '@databases/types/database';
import { ok, type Result } from 'neverthrow';

import { strictDatabaseValueValidator } from '../domain/entries/validators.ts';
import { assertEntryExists } from '../domain/guards/assert-entry-exists.ts';
import { assertFieldExists } from '../domain/guards/assert-field-exists.ts';
import { parse, tryMutate } from '../domain/result.ts';
import { generateValueItemIds } from '../domain/values/generate-value-item-ids.ts';
import {
  clearValueSchema,
  setValueInputSchema,
  type ClearValueInput,
  type SetValueInput
} from '../domain/values/schemas.ts';
import { clearValue } from '../handlers/value/clear-value.ts';
import { setValue } from '../handlers/value/set-value.ts';
import { type SdkError } from '../types.ts';

/**
 * Public API for reading and mutating database cell values.
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (ENTRY_NOT_FOUND, FIELD_NOT_FOUND)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 *
 * Values are **not** gated by `isLocked` — cell edits stay allowed
 * when the database structure is locked.
 */
export class ValueSdk implements ValueSdkContract {
  constructor(private readonly ydb: YDatabase) {}

  get({ entryId, fieldId }: { entryId: string; fieldId: string }) {
    const entry =
      entryId === DEFAULT_ENTRY_ID
        ? this.ydb.defaultEntry
        : this.ydb.entriesById[entryId];
    const value = entry?.getValue(fieldId);

    return value ? valueToDatabase(value.toJSON()) : undefined;
  }

  set(input: SetValueInput): Result<void, SdkError> {
    return parse(setValueInputSchema, input).andThen(
      ({ entryId, fieldId, value, resetValue }) =>
        parse(strictDatabaseValueValidator, generateValueItemIds(value)).andThen(
          (normalizedValue) =>
            this.resolveEntry(entryId)
              .andThen((entry) =>
                assertFieldExists(this.ydb, fieldId).map((field) => ({
                  entry,
                  field
                }))
              )
              .andThen(({ entry, field }) =>
                tryMutate(() =>
                  setValue(this.ydb, {
                    entry,
                    fieldId,
                    fieldType: field.type,
                    value: normalizedValue,
                    resetValue
                  })
                )
              )
        )
    );
  }

  clear(input: ClearValueInput): Result<void, SdkError> {
    return parse(clearValueSchema, input).andThen(({ entryId, fieldId }) =>
      this.resolveEntry(entryId)
        .andThen((entry) =>
          assertFieldExists(this.ydb, fieldId).map((field) => ({
            entry,
            field
          }))
        )
        .andThen(({ entry, field }) =>
          tryMutate(() =>
            clearValue(this.ydb, {
              entry,
              fieldId,
              fieldType: field.type
            })
          )
        )
    );
  }

  private resolveEntry(entryId: string) {
    return entryId === DEFAULT_ENTRY_ID
      ? ok(this.ydb.defaultEntry)
      : assertEntryExists(this.ydb, entryId);
  }
}
